import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
    LifeBuoy, RefreshCw, Filter, MessageCircle, AlertCircle,
} from "lucide-react";
import { TicketThreadDialog } from "@/pages/MyTickets";

/**
 * /admin/helpdesk — platform admin inbox for all tickets.
 *
 * Stats header (open / in_progress / resolved / closed counts), filter chips,
 * and a list of every ticket. Click → opens the same TicketThreadDialog used
 * by /tickets but with admin controls (status + priority + reply).
 */
const STATUS_META = {
    open: { label: "Open", cls: "border-amber-300 bg-amber-50 text-amber-900" },
    in_progress: { label: "In progress", cls: "border-ikb/40 bg-ikb/5 text-ikb" },
    resolved: { label: "Resolved", cls: "border-emerald-300 bg-emerald-50 text-emerald-800" },
    closed: { label: "Closed", cls: "border-border bg-secondary text-foreground/60" },
};

const PRIORITY_CLS = {
    high: "border-rose-400 bg-rose-50 text-rose-800",
    medium: "border-amber-300 bg-amber-50 text-amber-900",
    low: "border-border bg-secondary text-foreground/60",
};

export default function AdminHelpdesk() {
    const [items, setItems] = useState([]);
    const [stats, setStats] = useState({ open: 0, in_progress: 0, resolved: 0, closed: 0, total: 0 });
    const [filter, setFilter] = useState("");  // "" = all
    const [loading, setLoading] = useState(true);
    const [active, setActive] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/tickets", { params: filter ? { status: filter } : {} });
            setItems(data.items || []);
            setStats(data.stats || { open: 0, in_progress: 0, resolved: 0, closed: 0, total: 0 });
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    }, [filter]);

    useEffect(() => { load(); }, [load]);

    return (
        <div className="max-w-screen-xl mx-auto px-3 sm:px-6 py-4 sm:py-6">
            <div className="flex items-center gap-2 mb-4">
                <LifeBuoy className="w-5 h-5 text-ikb" />
                <h1 className="text-2xl font-display font-bold tracking-tight">Helpdesk</h1>
                <Button
                    onClick={load}
                    variant="outline"
                    size="sm"
                    className="ml-auto rounded-none"
                    disabled={loading}
                    data-testid="helpdesk-refresh"
                >
                    <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />
                    Refresh
                </Button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                <StatCard label="All" value={stats.total} accent="border-border bg-secondary/40 text-foreground/80" />
                <StatCard label="Open" value={stats.open} accent="border-amber-300 bg-amber-50/60 text-amber-900" />
                <StatCard label="In progress" value={stats.in_progress} accent="border-ikb/30 bg-ikb/5 text-ikb" />
                <StatCard label="Resolved" value={stats.resolved} accent="border-emerald-300 bg-emerald-50/60 text-emerald-800" />
                <StatCard label="Closed" value={stats.closed} accent="border-border bg-secondary text-foreground/60" />
            </div>

            <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="helpdesk-filters">
                <Filter className="w-3.5 h-3.5 text-foreground/60" />
                <FilterChip label="All" active={filter === ""} onClick={() => setFilter("")} testId="filter-all" />
                {Object.entries(STATUS_META).map(([k, v]) => (
                    <FilterChip
                        key={k}
                        label={v.label}
                        active={filter === k}
                        onClick={() => setFilter(k)}
                        testId={`filter-${k}`}
                    />
                ))}
            </div>

            {loading && items.length === 0 ? (
                <div className="text-foreground/60 text-sm py-12 text-center">Loading…</div>
            ) : items.length === 0 ? (
                <div className="border border-dashed border-border p-8 text-center text-foreground/60" data-testid="helpdesk-empty">
                    No tickets {filter ? `with status "${STATUS_META[filter]?.label}"` : ""}.
                </div>
            ) : (
                <div className="border border-border bg-white" data-testid="helpdesk-list">
                    <div className="grid grid-cols-12 gap-3 px-3 py-2 border-b border-border bg-secondary/40 text-[10px] font-mono uppercase tracking-widest text-foreground/60">
                        <div className="col-span-4">Description</div>
                        <div className="col-span-2">User</div>
                        <div className="col-span-2">Status</div>
                        <div className="col-span-1">Priority</div>
                        <div className="col-span-1">Replies</div>
                        <div className="col-span-2">Updated</div>
                    </div>
                    {items.map((t) => {
                        const sm = STATUS_META[t.status] || STATUS_META.open;
                        const pcls = PRIORITY_CLS[t.priority] || PRIORITY_CLS.medium;
                        const replyCount = (t.responses || []).length;
                        return (
                            <button
                                key={t.id}
                                onClick={() => setActive(t.id)}
                                className="w-full grid grid-cols-12 gap-3 px-3 py-3 border-b border-border last:border-b-0 hover:bg-secondary/30 text-left items-center"
                                data-testid={`helpdesk-ticket-${t.id}`}
                            >
                                <div className="col-span-4 min-w-0">
                                    <div className="text-sm font-medium truncate">{t.description}</div>
                                    <div className="text-[11px] text-foreground/60 truncate">
                                        {t.category || "other"}{t.page_url ? ` · ${t.page_url.replace(/^https?:\/\//, "").slice(0, 40)}` : ""}
                                    </div>
                                </div>
                                <div className="col-span-2 min-w-0">
                                    <div className="text-[12px] truncate">{t.user_name || "—"}</div>
                                    <div className="text-[10px] font-mono text-foreground/60 truncate">{t.user_email}</div>
                                </div>
                                <div className="col-span-2">
                                    <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border inline-flex ${sm.cls}`}>{sm.label}</span>
                                </div>
                                <div className="col-span-1">
                                    <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border inline-flex ${pcls}`}>{t.priority || "medium"}</span>
                                </div>
                                <div className="col-span-1 text-[11px] text-foreground/70 inline-flex items-center gap-1">
                                    {replyCount > 0 ? <><MessageCircle className="w-3 h-3" /> {replyCount}</> : <AlertCircle className="w-3 h-3 text-amber-500" title="No reply yet" />}
                                </div>
                                <div className="col-span-2 text-[11px] text-foreground/60">{new Date(t.updated_at).toLocaleString()}</div>
                            </button>
                        );
                    })}
                </div>
            )}

            <TicketThreadDialog
                ticketId={active}
                onClose={() => setActive(null)}
                onChanged={load}
                isAdminMode
            />
        </div>
    );
}

function StatCard({ label, value, accent }) {
    return (
        <div className={`border ${accent} px-3 py-2`} data-testid={`helpdesk-stat-${label.toLowerCase().replace(/\W+/g, "-")}`}>
            <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">{label}</div>
            <div className="text-2xl font-display font-bold">{value}</div>
        </div>
    );
}

function FilterChip({ label, active, onClick, testId }) {
    return (
        <button
            onClick={onClick}
            className={`text-[11px] font-mono uppercase tracking-widest px-2 py-1 border ${active ? "border-ikb bg-ikb text-white font-bold" : "border-border bg-white hover:bg-secondary text-foreground/70"}`}
            data-testid={testId}
        >
            {label}
        </button>
    );
}
